"use client";

import { Atom, Beaker, Box, Cpu, Droplet, Flame, Layers, Shield } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CreativePillars() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const pillars = [{
    name: "UNAPOLOGETIC",
    description: "We embrace the raw, unfiltered energy of live performance, stripping away the corporate polish to reveal the truth.",
    tensile: "100% RAW",
    temp: "HIGH",
    Icon: Layers
  }, {
    name: "DIGITAL PLAY",
    description: "Our playground is the internet, where we experiment with new formats, glitches, and interactive storytelling.",
    tensile: "LIMITLESS",
    temp: "ACTIVE",
    Icon: Droplet
  }, {
    name: "INDUSTRIAL",
    description: "Precision meets chaos. We build our digital infrastructure with the same rigor as a factory floor.",
    tensile: "STABLE",
    temp: "STEADY",
    Icon: Beaker
  }, {
    name: "STAGGERED",
    description: "Rhythm is everything. Our content is designed to disrupt your scroll with unexpected beats and visual shifts.",
    tensile: "DYNAMIC",
    temp: "FLUID",
    Icon: Box
  }, {
    name: "SULFUR",
    description: "High-visibility action zones that demand your attention, cutting through the noise of the modern web.",
    tensile: "VIBRANT",
    temp: "HOT",
    Icon: Atom
  }, {
    name: "BRUTALIST",
    description: "No soft edges. We believe in hard cuts, bold typography, and a design language that speaks for itself.",
    tensile: "SOLID",
    temp: "STARK",
    Icon: Shield
  }, {
    name: "GLITCH",
    description: "We find beauty in the errors, using digital artifacts to create a unique aesthetic that feels alive.",
    tensile: "UNPREDICTABLE",
    temp: "WARM",
    Icon: Cpu
  }, {
    name: "MECHANICAL",
    description: "Snappy, responsive, and precise. Every interaction is engineered to feel like a well-oiled machine.",
    tensile: "PRECISE",
    temp: "COOL",
    Icon: Flame
  }];
  return <section data-section-id="2254" id="creative-pillars" className="bg-background text-foreground py-24">
      <div className="mx-auto max-w-7xl px-8">
        
        <div ref={headerRef} className={`grid grid-cols-1 md:grid-cols-12 gap-12 mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="md:col-span-6">
            <p className="font-default text-xs tracking-[0.2em] uppercase mb-6" style={{
            color: "var(--ring)"
          }}>
              Core Philosophy
            </p>
            <h2 className="font-default font-bold text-6xl leading-[1.1] tracking-tighter text-foreground">
              The Oddhatter Manifesto.
            </h2>
          </div>
          <div className="md:col-span-6 md:pt-4">
            <p className="font-default text-xl leading-relaxed text-muted-foreground max-w-xl">
              We are an unapologetic digital playground. Our pillars define the rhythm, 
              the aesthetic, and the mechanical precision behind every piece of 
              entertainment we produce.
            </p>
          </div>
        </div>

        
        <div ref={gridRef} className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 transition-all duration-700 ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {pillars.map((pillar, i) => {
          const Icon = pillar.Icon;
          return <div data-index={i} key={pillar.name} className="group relative rounded-md border border-primary/30 bg-industrial-gray p-8 transition-all duration-300 ease-out hover:scale-[1.02]">
                
                <div className="mb-8 inline-flex h-16 w-16 items-center justify-center rounded-md" style={{
              backgroundColor: "var(--accent)"
            }}>
                  <Icon className="h-8 w-8 text-foreground" strokeWidth={2} />
                </div>

                
                <h3 className="font-default font-semibold text-xl tracking-widest text-foreground mb-4 uppercase">
                  {pillar.name}
                </h3>

                
                <p className="font-default text-base leading-relaxed text-muted-foreground mb-8">
                  {pillar.description}
                </p>

                
                <div className="pt-6 border-t border-dim-slate/20">
                  <p className="font-default text-xs tracking-widest uppercase" style={{
                color: "var(--muted-foreground)"
              }}>
                    <span style={{
                  color: "var(--ring)"
                }}>STATUS:</span>{" "}
                    <span className="text-foreground">{pillar.tensile}</span>
                    <span className="mx-2 opacity-50">·</span>
                    <span style={{
                  color: "var(--ring)"
                }}>MODE:</span>{" "}
                    <span className="text-foreground">{pillar.temp}</span>
                  </p>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}